import React from "react";
import { useTranslation } from "react-i18next";
import { VerticalDotsIcon } from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { useSelector } from "react-redux";

import { getUuidUser } from "@helpers/getUuidUser";

import { store as fidelity } from "../../../../../components/VerticalLayout/store";
import { useFetchGlobal } from "../../../../../api/hooks";

import { RootState } from "../../../../../redux/store";
import { getArrayOfRows, getAssociatedIds } from "../../helpers";
import { ConfirmationArchive } from "../../ConfirmationArchive";

import { ActionColumnProps } from "./types";
import { useBulkActivateFidelity } from "./useBulkActivateFidelity";
import { useBulkArchiveClick } from "./useBulkArchiveClick";
import TabSwitchItems from "./components/TabSwitchItems";
import BulkArchiveMenuItem from "./components/BulkArchiveMenuItem";

export function ActionColumnWrapper({
    selectedRows,
    setDataIsUpdated,
    setIsModalOpened,
    isModalOpened,
    setIsFidelityArchived,
    isFidelityArchived,
    setIsAddFidelityClicked,
    setIsModalOpenedSupport,
    setLimit,
}: ActionColumnProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const shopsFidelityData = useSelector(
        (s: RootState) => s.fidelity.shopsFidelityData
    );
    const archivedFidelityFranchise = useSelector(
        (s: RootState) => s.fidelity.archivedFidelityFranchise
    );
    const isEdited = useSelector((s: RootState) => s.fidelity.isEdited);
    const isActionsClicked = useSelector(
        (s: RootState) => s.fidelity.isActionsClicked
    );
    const modify = useSelector((s: RootState) => s.fidelity.modify);
    const isSupportCodeValid = useSelector(
        (s: RootState) => s.fidelity.isSupportCodeValid
    );
    const dataActive = useSelector((s: RootState) => s.fidelity.dataActive);
    const { franchiseID, oneShop, userID, operatorID } = useSnapshot(fidelity);
    const uuidUser = getUuidUser();

    const [isArchiveModalOpened, setIsArchiveModalOpened] = React.useState(
        false
    );
    const [isFidelityClicked, setIsFidelityClicked] = React.useState(false);

    const dataArchive = React.useMemo(() => {
        if (!oneShop) return getAssociatedIds(shopsFidelityData);
        const ids = new Set<string>();
        const franchiseData =
            archivedFidelityFranchise?.othersData?.archivedFranchise?.data;
        if (franchiseData) {
            for (const el of franchiseData) ids.add(el.Id);
        }
        return [...ids];
    }, [oneShop, shopsFidelityData, archivedFidelityFranchise]);

    React.useMemo(() => getArrayOfRows(selectedRows, t), [selectedRows, t]);

    const activate = useBulkActivateFidelity({
        selectedRows,
        oneShop,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        setLimit,
        t,
    });

    const handleBulkArchiveClick = useBulkArchiveClick({
        selectedRows,
        oneShop,
        isFidelityArchived,
        dataArchive,
        dataActive,
        isModalOpened,
        setIsModalOpened,
        setIsModalOpenedSupport,
        setIsArchiveModalOpened,
        setIsFidelityClicked,
        activate,
        t,
    });

    const showConfirmArchive =
        isArchiveModalOpened || (isFidelityClicked && isSupportCodeValid);

    return (
        <React.Fragment>
            <div>
                <div className="dropdown">
                    <VerticalDotsIcon
                        id="dropdownMenuButton1"
                        className="cmn_drp_FRmenu"
                        data-bs-toggle="dropdown"
                        style={{ cursor: "pointer" }}
                        height={25}
                        width={25}
                        fill="black"
                    />
                    <div
                        className="dropdown-menu"
                        aria-labelledby="dropdownMenuButton"
                    >
                        <TabSwitchItems
                            isEdited={isEdited}
                            isActionsClicked={isActionsClicked}
                            isFidelityArchived={isFidelityArchived}
                            setIsFidelityArchived={setIsFidelityArchived}
                            setIsAddFidelityClicked={setIsAddFidelityClicked}
                            setDataIsUpdated={setDataIsUpdated}
                            t={t}
                        />
                        {modify === true ? (
                            <BulkArchiveMenuItem
                                selectedRows={selectedRows}
                                oneShop={oneShop}
                                isFidelityArchived={isFidelityArchived}
                                dataArchive={dataArchive}
                                onClick={handleBulkArchiveClick}
                                t={t}
                            />
                        ) : null}
                    </div>
                </div>
            </div>

            {showConfirmArchive ? (
                <ConfirmationArchive
                    isModalOpened={showConfirmArchive}
                    setIsModalOpened={setIsArchiveModalOpened}
                    setDataIsUpdated={setDataIsUpdated}
                    selectedRows={selectedRows}
                />
            ) : null}
        </React.Fragment>
    );
}
